import React, {
	FC,
	createContext,
	useContext,
	useEffect,
	useState,
} from 'react'
import {
	Client,
	Conversation,
	ConversationUpdateReason,
	JSONValue,
	User,
} from '@twilio/conversations'

import useIsMounted from '../../useIsMounted'
import useChannelHelper from './useChannelHelper/useChannelHelper'
import useChatHelper, { ChannelPatientData } from './useChatHelper'
import { ChatChannel, ChatEvents, MessageProps } from '@app/types/chat'

export type ConversationKeyItems = { [sid: string]: Conversation }

interface ConversationUpdatedData {
	conversation: Conversation
	updateReasons: ConversationUpdateReason[]
}

interface ChatContextProps {
	sendMessage: (message: string, messageAttributes?: JSONValue) => void
	setSearchValue: (value: string) => void
	onSetChannel: (sid: string) => void
	channels: ChatChannel[]
	allChannels: ChatChannel[]
	subscribedChannel?: Conversation
	listIsReady: boolean
	isChat: boolean
	lastSeenIndex: number
	channelKeys: ChannelPatientData
	allSubscribedChannel: ConversationKeyItems
	channelMessages: MessageProps[]
	channelsAreLoading?: boolean
	subscribedChannelsAreLoading?: boolean
	getPatientChat: (coachId: string) => Promise<string>
	patientChat?: ChatChannel
	setPinnedChannelsSids: React.Dispatch<React.SetStateAction<string[]>>
	pinnedChannelsSids: string[]
	searchValue: string
	userIdentity: string
	lastSeenPatientIndex: number
	isPatientOnline: boolean
	userModelsForParticipants: User[]
	channelDataIsLoading: boolean
	clearChannel: () => void
	sendBulkMessage: (recipients: string[], message: string) => Promise<number>
}

const ChatContext = createContext<ChatContextProps>(null!)

// eslint-disable-next-line react/prop-types
export const ChatProvider: FC = ({ children }) => {
	const isMounted = useIsMounted()
	const [chatClient, setChatClient] = useState<Client>()
	const [subscribedChannel, setSubscribedChannel] = useState<Conversation>()
	const [allSubscribedChannel, setAllSubscribedChannel] =
		useState<ConversationKeyItems>({})
	const [listIsReady, setListIsReady] = useState(false)
	const [subscribedChannelsAreLoading, setSubscribedChannelsAreLoading] =
		useState<boolean>(true)
	const userIdentity = chatClient?.user.identity || ''

	const {
		lastSeenIndex,
		lastSeenPatientIndex,
		channelMessages,
		isPatientOnline,
		userModelsForParticipants,
		channelDataIsLoading,
		resetChannel,
	} = useChannelHelper(userIdentity, subscribedChannel)

	const {
		setSearchValue,
		fetchUserChatToken,
		token,
		channels,
		channelKeys,
		isChat,
		channelsAreLoading,
		setPinnedChannelsSids,
		pinnedChannelsSids,
		getPatientChat,
		patientChat,
		searchValue,
		allChannels,
	} = useChatHelper()

	const getChatClient = (): Promise<Client | undefined> => {
		if (!chatClient && !token) {
			throw Error
		}
		if (!chatClient && token) {
			return Client.create(token).then((client: Client) => {
				setChatClient(client)
				return chatClient
			})
		}

		return Promise.resolve(chatClient)
	}

	const getSubscribedChannels = async (): Promise<void> => {
		const subscribedChannels =
			await chatClient?.getSubscribedConversations()

		if (subscribedChannels?.items.length) {
			const conversationChannels = subscribedChannels.items.reduce(
				(acc: ConversationKeyItems, item: Conversation) => {
					if (item.lastMessage) {
						acc[item.sid] = item
					}
					return acc
				},
				{}
			)

			setAllSubscribedChannel(conversationChannels)
		}
	}

	const onSetChannel = (sid: string): void => {
		if (!sid) return

		if (allSubscribedChannel && allSubscribedChannel[sid]) {
			setSubscribedChannel(allSubscribedChannel[sid])
		} else {
			chatClient
				?.getConversationBySid(sid)
				.then(data => setSubscribedChannel(data))
				// eslint-disable-next-line no-console
				.catch(e => console.warn('Error getting channels', e))
		}
		setListIsReady(true)
	}

	const clearChannel = (): void => {
		setSubscribedChannel(undefined)

		resetChannel()
	}

	const sendMessage = (
		message: string,
		messageAttributes?: JSONValue
	): void => {
		subscribedChannel?.sendMessage(message, messageAttributes)
	}

	const sendBulkMessage = async (recipients: string[], message: string): Promise<number> => {
		const foundChannels = allChannels.filter(chan => recipients.includes(chan.provisioning_id));
		const subscribedChannels =
				await chatClient?.getSubscribedConversations()
		const conversationChannels = subscribedChannels?.items.reduce(
				(acc: ConversationKeyItems, item: Conversation) => {
						acc[item.sid] = item
					return acc
				},
				{}
		)
		let sentMessagesLength = 0;
		if (conversationChannels) {
			await Promise.all(foundChannels.map(chan => {
				if (conversationChannels[chan.channel_sid]) {
					conversationChannels[chan.channel_sid].sendMessage(message)
					sentMessagesLength++;
				}
				return chan;
			}))
		}
		return sentMessagesLength;
	}

	const updateToken = (getToken: () => Promise<string>): void => {
		getToken().then(chatClient?.updateToken)
	}

	const addTokenListener = (getToken: () => Promise<string>): Client => {
		if (!chatClient) {
			throw new Error()
		}
		chatClient?.on(ChatEvents.TOKEN_ABOUT_TO_EXPIRE, () => {
			updateToken(getToken)
		})

		chatClient?.on(ChatEvents.TOKEN_EXPIRED, () => {
			updateToken(getToken)
		})

		return chatClient
	}

	useEffect(() => {
		if (token) {
			getChatClient().then(() => setListIsReady(true))
		}
	}, [token])

	useEffect(() => {
		if (!chatClient) return

		const handleConversationUpdated = (
			conversationUpdatedData: ConversationUpdatedData
		): void => {
			const conversationSid = conversationUpdatedData?.conversation?.sid

			if (!conversationSid) return

			setAllSubscribedChannel(channels => {
				return {
					...channels,
					[conversationSid]: conversationUpdatedData.conversation,
				}
			})
		}
		getSubscribedChannels().then(() => {
			addTokenListener(fetchUserChatToken)
			chatClient.on(
				ChatEvents.CONVERSATION_UPDATED,
				handleConversationUpdated
			)

			isMounted() && setSubscribedChannelsAreLoading(false)
		})

		return () => {
			chatClient.off(
				ChatEvents.CONVERSATION_UPDATED,
				handleConversationUpdated
			)
		}
	}, [chatClient])

	const value = {
		sendMessage,
		onSetChannel,
		setSearchValue,
		getPatientChat,
		lastSeenIndex,
		channelMessages,
		channels,
		listIsReady,
		subscribedChannel,
		isChat,
		channelKeys,
		allSubscribedChannel,
		channelsAreLoading,
		subscribedChannelsAreLoading,
		setPinnedChannelsSids,
		pinnedChannelsSids,
		patientChat,
		searchValue,
		allChannels,
		userIdentity,
		lastSeenPatientIndex,
		isPatientOnline,
		userModelsForParticipants,
		channelDataIsLoading,
		clearChannel,
		sendBulkMessage,
	}

	return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export const useChat = (): ChatContextProps => {
	const value = useContext(ChatContext)
	// eslint-disable-next-line no-throw-literal
	if (!value) throw 'useChat must be used inside ChatProvider'

	return value
}
